import { useState, useEffect } from "react";
import { X, Settings } from "lucide-react";

/**
 * Phase B audit fix B3 — UK PECR + GDPR-compliant cookie banner.
 *
 * Pattern: opt-in for non-essential cookies, reject-by-default, per-category
 * toggles. Choice persisted in localStorage under `tm-consent-v1`.
 *
 * Any analytics SDK (PostHog/Mixpanel — Phase C T-C22) MUST read getConsent()
 * before initialising and re-check on consent change.
 */

const COOKIE_KEY = "tm-consent-v1";

export type ConsentCategory = "essential" | "analytics" | "marketing";
export interface CookieConsent {
  essential: true;              // always true — required for app to function
  analytics: boolean;            // PostHog/Mixpanel/etc
  marketing: boolean;            // retargeting / Brevo behavioural
  consented_at: string;          // ISO timestamp
}

const DEFAULT_CONSENT: CookieConsent = {
  essential: true,
  analytics: false,
  marketing: false,
  consented_at: "",
};

export function getConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(COOKIE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookieConsent;
  } catch {
    return null;
  }
}

export function setConsent(c: Partial<CookieConsent>): CookieConsent {
  const merged: CookieConsent = {
    ...DEFAULT_CONSENT,
    ...c,
    essential: true,
    consented_at: new Date().toISOString(),
  };
  localStorage.setItem(COOKIE_KEY, JSON.stringify(merged));
  // Fire a custom event so analytics SDKs can re-init on consent change.
  window.dispatchEvent(new CustomEvent("tm-consent-changed", { detail: merged }));
  return merged;
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [marketingOn, setMarketingOn] = useState(false);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
  }, []);

  const acceptAll = () => {
    setConsent({ analytics: true, marketing: true });
    setVisible(false);
  };

  const rejectNonEssential = () => {
    setConsent({ analytics: false, marketing: false });
    setVisible(false);
  };

  const saveSelection = () => {
    setConsent({ analytics: analyticsOn, marketing: marketingOn });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 px-4 py-3 sm:px-6"
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="false"
    >
      <div className="max-w-4xl mx-auto bg-[#1a1a1a] border border-white/10 rounded-xl px-5 py-4 shadow-lg space-y-3">
        {!manageOpen ? (
          <>
            <p className="text-sm text-white/70">
              We use <strong className="text-white">essential cookies</strong> for authentication and app function.
              With your consent, we may also use <strong className="text-white">analytics cookies</strong> to understand how Time Miners is used
              and <strong className="text-white">marketing cookies</strong> for email follow-ups.{" "}
              <a href="/privacy" className="text-[#C9A96E] hover:underline">Privacy Policy</a>
            </p>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={() => setManageOpen(true)}
                className="flex items-center gap-1 text-xs text-white/60 hover:text-white/90 px-3 py-2 transition-colors"
              >
                <Settings size={14} />
                Manage
              </button>
              <button
                onClick={rejectNonEssential}
                className="text-xs font-semibold text-white/80 hover:text-white border border-white/20 hover:border-white/40 px-4 py-2 rounded-lg transition-colors"
              >
                Reject non-essential
              </button>
              <button
                onClick={acceptAll}
                className="text-xs font-bold text-black bg-[#C9A96E] hover:bg-[#B8945A] px-4 py-2 rounded-lg transition-colors"
              >
                Accept all
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Cookie preferences</h3>
              <button
                onClick={() => setManageOpen(false)}
                aria-label="Close manage panel"
                className="text-white/40 hover:text-white/80"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3 text-xs text-white/70">
              <Toggle
                label="Essential (required)"
                desc="Authentication, session, security. Cannot be disabled."
                checked
                disabled
                onChange={() => {}}
              />
              <Toggle
                label="Analytics"
                desc="Help us understand how Time Miners is used. We do not sell this data."
                checked={analyticsOn}
                onChange={setAnalyticsOn}
              />
              <Toggle
                label="Marketing"
                desc="Allow personalised email follow-ups (e.g. trial reminders)."
                checked={marketingOn}
                onChange={setMarketingOn}
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={() => setManageOpen(false)}
                className="text-xs text-white/60 hover:text-white/90 px-3 py-2"
              >
                Back
              </button>
              <button
                onClick={saveSelection}
                className="text-xs font-bold text-black bg-[#C9A96E] hover:bg-[#B8945A] px-4 py-2 rounded-lg transition-colors"
              >
                Save preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Toggle(props: {
  label: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className={`flex items-start gap-3 ${props.disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 accent-[#C9A96E]"
      />
      <div className="flex-1">
        <p className="text-white font-medium">{props.label}</p>
        <p className="text-white/50 text-[11px] leading-tight mt-0.5">{props.desc}</p>
      </div>
    </label>
  );
}
